import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "./ui/button";

// Alluz Logo Component
const AlluzLogo = ({ className = "", size = 50 }) => (
  <img 
    src="/logo-alluz.png" 
    alt="Alluz Energia" 
    className={className}
    style={{ width: size, height: size, objectFit: 'contain' }}
  />
);

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#sobre", label: "Sobre" },
    { href: "#vantagens", label: "Vantagens" },
    { href: "#missao", label: "Missão" },
    { href: "#contato", label: "Contato" },
  ];

  return (
    <>
      {/* Fixed small navbar in top right */}
      <motion.nav
        data-testid="navbar"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className={`fixed top-3 right-4 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-white/95 shadow-lg shadow-[#FFB414]/10"
            : "bg-white/70"
        } backdrop-blur-xl rounded-full px-4 py-2 border border-[#FFB414]/20`}
      >
        <div className="flex items-center gap-4">
          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-4">
            {navLinks.map((link) => (
              <motion.a
                key={link.href}
                href={link.href}
                className="relative text-[#92400E]/80 hover:text-[#92400E] font-semibold text-sm transition-colors group"
                whileHover={{ y: -1 }}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#FFB414] to-[#FF8C00] group-hover:w-full transition-all duration-300" />
              </motion.a>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-2">
            <motion.a
              href="https://simulador.alluzenergia.com.br"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                className="btn-glow text-white font-bold px-4 py-2 text-sm rounded-full border-0 flex items-center gap-2"
                data-testid="nav-simular-btn"
              >
                <AlluzLogo size={20} className="!filter-none !animate-none" />
                Simular
              </Button>
            </motion.a>
            <motion.a
              href="https://wa.me/5544988574869"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                variant="outline"
                className="border-2 border-[#FFB414]/50 text-[#92400E] font-bold px-4 py-2 text-sm rounded-full hover:bg-[#FFB414]/10 hover:border-[#FFB414] transition-all flex items-center gap-2"
                data-testid="nav-contato-btn"
              >
                <Phone className="w-3 h-3 text-[#FF8C00]" />
                Contato
              </Button>
            </motion.a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-[#92400E] hover:bg-[#FFB414]/10 rounded-full transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className="fixed top-16 right-4 z-40 w-[280px] bg-white/95 backdrop-blur-xl rounded-2xl p-5 lg:hidden shadow-xl shadow-[#FFB414]/20 border border-[#FFB414]/20"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-[#92400E] font-semibold py-2.5 px-3 rounded-xl hover:bg-[#FFB414]/10 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <div className="border-t border-[#FFB414]/20 pt-3 mt-2 space-y-2">
                <a
                  href="https://simulador.alluzenergia.com.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Button className="btn-glow text-white font-bold w-full flex items-center justify-center gap-2 rounded-xl">
                    <AlluzLogo size={22} className="!filter-none !animate-none" />
                    Simular Sistema
                  </Button>
                </a>
                <a
                  href="https://wa.me/5544988574869"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Button
                    variant="outline"
                    className="border-2 border-[#FFB414] text-[#92400E] font-bold w-full rounded-xl"
                    data-testid="mobile-contato-btn"
                  >
                    <Phone className="w-4 h-4 mr-2 text-[#FF8C00]" />
                    Fale Conosco
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
